import {ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input, OnInit} from '@angular/core';
import {KPI} from '../../../../@core/api';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {
  NbButtonModule,
  NbCardModule,
  NbDialogService,
  NbIconModule,
  NbLayoutModule,
  NbTableModule
} from '@nebular/theme';
import {GROUP_PERMISSION_EDIT_QUOTE_PART, QuotePartItem} from '../../../../@core/api/models';
import {CommonModule} from '@angular/common';
import {combineLatest, filter} from 'rxjs';
import {ConfirmationDialogComponent} from '../../confirmation-dialog/confirmation-dialog.component';
import {ErrorService, QuotePartService, ScopeService, UsersService, SaveToastService} from '../../../../@core/utils';
import { SiteQuotePartDialogComponent } from '../quote-part-dialog/site-quote-part-dialog.component';
import {map} from "rxjs/operators";

@Component({
  selector: 'app-site-quote-part',
  standalone: true,
  templateUrl: './site-quote-part.component.html',
  styleUrl: './site-quote-part.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslateModule,
    NbLayoutModule,
    NbCardModule,
    NbTableModule,
    CommonModule,
    NbIconModule,
    NbButtonModule,
    
  ],
})
export class SiteQuotePartComponent implements OnInit {

  @Input() public perimeter: KPI.Perimeter;

  usersService = inject(UsersService)

  public sections: QuotePartItem[] = [];
  public isAllowEditable: boolean = false;
  public loading: boolean = false;

  constructor(
    protected translate: TranslateService,
    private nbDialogService: NbDialogService,
    private cdRef: ChangeDetectorRef,
    private errorService: ErrorService,
    private quotePartService: QuotePartService,
    private scopeService: ScopeService,
    private saveToast: SaveToastService
  ) {
  }

  ngOnInit(): void {
    this.loadData();
  }

  private loadData() {
    this.loading = true;
    combineLatest([
      this.usersService.hasCurrentUserPermissions([GROUP_PERMISSION_EDIT_QUOTE_PART]),
      this.quotePartService.getQuotePartBySiteId(this.perimeter.id).pipe(
        filter((quoteParts) => !!quoteParts),
        map((quoteParts) => quoteParts.filter(qp => !qp.is_other)),
      ),
      this.scopeService.investmentSponsors$
    ])
      .subscribe(([edit_quote_part, quoteParts, sponsors]) => {
        this.isAllowEditable = edit_quote_part;
        quoteParts.forEach((quotePart) => {
          quotePart.sponsors.forEach((sponsor) => {
            sponsor['name'] = sponsors[sponsor.sponsor_id]?.name;
          });
        });
        this.sections = [
          ...quoteParts
        ];
        this.loading = false
        this.cdRef.detectChanges();
      });
  }

  onDeleteSection(section: QuotePartItem) {
    const label = this.translate.instant('quote_part.delete_confirmation', {quotepart: section.name});
    const confirmDialog = this.nbDialogService.open(
      ConfirmationDialogComponent,
      {
        context: {label: label},
        autoFocus: false,
      },
    );
    confirmDialog.componentRef.instance.isConfirm.subscribe(() => {
      this.saveToast.showSaving();
      this.quotePartService.deleteQuotePart(section.id).subscribe(
        () => {
          this.loadData();
          this.saveToast.showSaved();
        },
        (error) => {
          this.saveToast.close();
          this.errorService.handleError(error, true);
        }
      )
    });
  }

  showDetailOrEdit(key: number, $event) {
    const ref = this.nbDialogService.open(SiteQuotePartDialogComponent, {
      dialogClass: 'custom-dialog',
      context: {
        siteId: this.perimeter.id,
        id: key,
        readOnly: !this.isAllowEditable
      } as Partial<SiteQuotePartDialogComponent>
    })
    $event.target.blur();
    ref.onClose
      .pipe(filter((response: { reload?: boolean }) => response?.reload))
      .subscribe(() => {
        this.loadData();
        this.saveToast.showSaved();
      });
  }

  openAddNewPopup(event: Event) {
    event.preventDefault();
    const ref = this.nbDialogService.open(SiteQuotePartDialogComponent, {
      dialogClass: 'custom-dialog',
      context: {
        siteId: this.perimeter.id
      } as Partial<SiteQuotePartDialogComponent>
    })
    ref.onClose
      .pipe(filter((response: { reload?: boolean }) => response?.reload))
      .subscribe(() => {
        this.loadData();
        this.saveToast.showSaved();
      });
  }
}
